import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, CircularProgress, Alert, Stack, LinearProgress, Divider, Tooltip, Chip,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Logout as LogoutIcon, AccountBalance as BankIcon, Speed as LimitIcon,
  PictureAsPdf as PdfIcon, TaskAlt as DoneIcon, Inbox as EmptyIcon,
} from '@mui/icons-material';
import { aprovadorApi, AcessoInfo, AprovadorError, Panel, PanelItem, Review, brl, dataBR, horaBR } from '@/services/aprovador';
import CodeGate from '@/components/aprovar/CodeGate';
import ItemCard from '@/components/aprovar/ItemCard';
import Filtros, { FiltrosState, FILTROS_INICIAIS, Periodo } from '@/components/aprovar/Filtros';
import ConferirDialog from '@/components/aprovar/ConferirDialog';
import PagueiDialog from '@/components/aprovar/PagueiDialog';
import RejeitarDialog from '@/components/aprovar/RejeitarDialog';
import ItemDetail from '@/components/aprovar/ItemDetail';
import BulkApproveDialog from '@/components/aprovar/BulkApproveDialog';
import TransferirEfiDialog from '@/components/aprovar/TransferirEfiDialog';
import { useAutoUpdate } from '@/hooks/useAutoUpdate';

/** O token vem no fragmento (#): nunca vai ao servidor, a log de proxy nem a Referer. */
function readToken(): string {
  return decodeURIComponent((window.location.hash || '').replace(/^#\/?/, '')).trim();
}

const num = (s: string) => {
  const n = parseFloat(String(s).replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

function noPeriodo(i: PanelItem, p: Periodo, f: FiltrosState, today: string, in7: string): boolean {
  const d = i.dueDate;
  switch (p) {
    case 'vencidos': return i.dueStatus === 'vencido';
    case 'hoje': return i.dueStatus === 'hoje';
    case 'ate_hoje': return i.dueStatus === 'vencido' || i.dueStatus === 'hoje';
    case '7dias': return !!d && d > today && d <= in7;
    case 'intervalo': return !!d && (!f.de || d >= f.de) && (!f.ate || d <= f.ate);
    default: return true;
  }
}

const Kpi: React.FC<{ titulo: string; valor: string; sub?: React.ReactNode; cor?: string; icon?: React.ReactNode; children?: React.ReactNode; onClick?: () => void }> =
  ({ titulo, valor, sub, cor, icon, children, onClick }) => (
    <Paper variant="outlined" onClick={onClick}
      sx={{ p: 1.5, borderRadius: 2, cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { borderColor: 'primary.main' } : undefined }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
        {icon}<Typography variant="caption" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>{titulo}</Typography>
      </Box>
      <Typography variant="h6" fontWeight={700} sx={{ color: cor, fontVariantNumeric: 'tabular-nums', mt: 0.25 }}>{valor}</Typography>
      {sub && <Typography variant="caption" color="text.secondary" component="div">{sub}</Typography>}
      {children}
    </Paper>
  );

const AprovarPage: React.FC = () => {
  const [token, setToken] = useState(readToken);
  const [fase, setFase] = useState<'carregando' | 'codigo' | 'painel' | 'invalido'>('carregando');
  const [acesso, setAcesso] = useState<AcessoInfo | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [avisoGeral, setAvisoGeral] = useState<string | null>(null);
  const [transferindoEfi, setTransferindoEfi] = useState(false);
  const [panel, setPanel] = useState<Panel | null>(null);
  const [atualizando, setAtualizando] = useState(false);
  const [filtros, setFiltros] = useState<FiltrosState>(FILTROS_INICIAIS);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [conferir, setConferir] = useState<PanelItem | null>(null);
  const [paguei, setPaguei] = useState<PanelItem | null>(null);
  const [rejeitar, setRejeitar] = useState<PanelItem[] | null>(null);
  const [detalhe, setDetalhe] = useState<PanelItem | null>(null);
  const [conferirReview, setConferirReview] = useState<Review | null>(null);
  const [lote, setLote] = useState<PanelItem[] | null>(null);

  useEffect(() => {
    document.title = 'Pagamentos — aprovação';
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    // Sem zoom no painel (pinça, toque duplo e o zoom do iOS ao focar um campo).
    // Android respeita o user-scalable=no; o iOS ignora para pinça, por isso o gesturestart.
    const vp = document.querySelector('meta[name="viewport"]');
    const vpAntes = vp?.getAttribute('content') || 'width=device-width, initial-scale=1.0';
    vp?.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
    const touchAntes = document.documentElement.style.touchAction;
    document.documentElement.style.touchAction = 'pan-x pan-y';
    const semGesto = (e: Event) => e.preventDefault();
    document.addEventListener('gesturestart', semGesto);
    document.addEventListener('gesturechange', semGesto);
    const onHash = () => setToken(readToken());
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
      document.head.removeChild(meta);
      vp?.setAttribute('content', vpAntes);
      document.documentElement.style.touchAction = touchAntes;
      document.removeEventListener('gesturestart', semGesto);
      document.removeEventListener('gesturechange', semGesto);
    };
  }, []);

  const emVoo = useRef(false);
  /** `silent`: atualização automática — sem barra de progresso e sem mostrar erro de rede passageiro. */
  const carregarPainel = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = !!(opts && opts.silent === true);
    if (silent && emVoo.current) return;
    emVoo.current = true;
    if (!silent) setAtualizando(true);
    try {
      const p = await aprovadorApi.painel(token);
      setPanel(p);
      setFase('painel');
      setErroGeral(null);
      setSel((s) => new Set([...s].filter((id) => p.items.some((i) => i.id === id))));
    } catch (e) {
      const err = e as AprovadorError;
      if (err.status === 401) { setFase('carregando'); iniciar(); } // eslint-disable-line @typescript-eslint/no-use-before-define
      else if (err.code === 'invalid_link') setFase('invalido');
      else if (!silent || err.code !== 'network') setErroGeral(err.message);
    } finally {
      emVoo.current = false;
      setAtualizando(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const iniciar = useCallback(async () => {
    if (!token) { setFase('invalido'); return; }
    try {
      const a = await aprovadorApi.acesso(token);
      setAcesso(a);
      if (a.verified) await carregarPainel();
      else setFase('codigo');
    } catch (e) {
      const err = e as AprovadorError;
      if (err.code === 'invalid_link') setFase('invalido');
      else { setErroGeral(err.message); setFase('invalido'); }
    }
  }, [token, carregarPainel]);

  useEffect(() => { iniciar(); }, [iniciar]);

  // Dados sempre frescos: ao voltar para a aba/foco (ela alterna com o app do banco) e a cada ~1 min.
  useEffect(() => {
    if (fase !== 'painel') return undefined;
    const tick = () => { if (document.visibilityState === 'visible') carregarPainel({ silent: true }); };
    const timer = window.setInterval(tick, 60 * 1000);
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    window.addEventListener('online', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
      window.removeEventListener('online', tick);
    };
  }, [fase, carregarPainel]);

  // Versão nova do painel: recarrega sozinho, mas nunca no meio de uma ação
  // (confirmação/pagamento/rejeição/detalhes abertos, ou digitando o código).
  useAutoUpdate(fase === 'codigo' || !!conferir || !!paguei || !!rejeitar || !!lote || !!detalhe);

  const sair = async () => {
    try { await aprovadorApi.sair(token); } catch { /* ignore */ }
    setPanel(null);
    setFase('carregando');
    iniciar();
  };

  const sessaoPerdida = () => {
    setConferir(null); setPaguei(null); setRejeitar(null); setLote(null); setDetalhe(null);
    setFase('carregando'); iniciar();
  };

  // Item resolvido (pago/rejeitado) some da lista: fecha a página de detalhes dele.
  useEffect(() => {
    if (!detalhe || !panel) return;
    const atual = panel.items.find((i) => i.id === detalhe.id);
    if (!atual) {
      setDetalhe(null);
      if (window.history.state && window.history.state.aprovarDetalhe) window.history.back();
    } else if (atual !== detalhe) setDetalhe(atual);
  }, [panel]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = panel?.items || [];
  const today = panel?.today || '';
  const in7 = panel?.in7days || '';

  const contagem = useMemo(() => {
    const c: Partial<Record<Periodo, number>> = {};
    (['todos', 'ate_hoje', 'vencidos', 'hoje', '7dias'] as Periodo[]).forEach((p) => {
      c[p] = items.filter((i) => noPeriodo(i, p, filtros, today, in7)).length;
    });
    return c;
  }, [items, filtros, today, in7]);

  const favorecidos = useMemo(
    () => [...new Set(items.map((i) => i.beneficiaryName).filter(Boolean) as string[])].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [items],
  );

  const filtrados = useMemo(() => {
    const busca = filtros.busca.trim().toLowerCase();
    const min = num(filtros.valorMin);
    const max = num(filtros.valorMax);
    const out = items.filter((i) => {
      if (!noPeriodo(i, filtros.periodo, filtros, today, in7)) return false;
      if (filtros.status === 'aguardando' && (i.status !== 'pending' || i.awaitingConfirmation)) return false;
      if (filtros.status === 'conferido' && !i.awaitingConfirmation) return false;
      if (filtros.status === 'processando' && i.status !== 'processing') return false;
      if (filtros.status === 'repetidos' && !i.duplicateCount) return false;
      if (filtros.via !== 'todas' && i.payVia !== filtros.via) return false;
      if (filtros.forma === 'pix_chave' && i.subtype !== 'pix_chave') return false;
      if (filtros.forma === 'pix_qr' && i.kind !== 'pix_copia_cola') return false;
      if (filtros.forma === 'boleto' && i.subtype !== 'boleto') return false;
      if (filtros.forma === 'arrecadacao' && i.subtype !== 'arrecadacao') return false;
      if (filtros.favorecido && i.beneficiaryName !== filtros.favorecido) return false;
      if (min != null && i.amount < min) return false;
      if (max != null && i.amount > max) return false;
      if (busca) {
        const hay = [i.beneficiaryName, i.description, i.pixKey, i.barcode, i.externalId, i.beneficiaryDocument, i.amount.toFixed(2).replace('.', ',')]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(busca)) return false;
      }
      return true;
    });
    const dueKey = (i: PanelItem) => i.dueDate || '9999-12-31';
    out.sort((a, b) => {
      switch (filtros.ordem) {
        case 'venc_desc': return dueKey(b).localeCompare(dueKey(a));
        case 'valor_desc': return b.amount - a.amount;
        case 'valor_asc': return a.amount - b.amount;
        case 'fav': return (a.beneficiaryName || '').localeCompare(b.beneficiaryName || '', 'pt-BR');
        default: return dueKey(a).localeCompare(dueKey(b)) || b.amount - a.amount;
      }
    });
    return out;
  }, [items, filtros, today, in7]);

  const soma = (arr: PanelItem[]) => arr.reduce((s, i) => s + i.amount, 0);
  const vencidos = items.filter((i) => i.dueStatus === 'vencido');
  const hoje = items.filter((i) => i.dueStatus === 'hoje');
  const prox7 = items.filter((i) => i.dueDate && i.dueDate > today && i.dueDate <= in7);
  const selecionados = items.filter((i) => sel.has(i.id));
  const totalSel = soma(selecionados);
  const selApi = selecionados.filter((i) => i.payVia === 'api' && i.status === 'pending');
  const selApp = selecionados.filter((i) => i.payVia === 'app');
  const totalSelApi = soma(selApi);
  const saldoDisp = panel?.saldo.disponivel;

  const toggle = (id: string) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const todosVisiveisSel = filtrados.length > 0 && filtrados.filter((i) => i.status !== 'processing').every((i) => sel.has(i.id));

  // ---------------- telas ----------------
  if (fase === 'carregando') {
    return <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f3f5f8' }}><CircularProgress /></Box>;
  }
  if (fase === 'invalido') {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f3f5f8', p: 2 }}>
        <Paper variant="outlined" sx={{ p: 4, maxWidth: 400, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom>Link inválido</Typography>
          <Typography variant="body2" color="text.secondary">
            {erroGeral || 'Este link não existe mais ou foi revogado. Peça um novo link ao responsável.'}
          </Typography>
        </Paper>
      </Box>
    );
  }
  if (fase === 'codigo' && acesso) {
    return <CodeGate token={token} info={acesso} onVerified={() => { setFase('carregando'); carregarPainel(); }} />;
  }
  if (!panel) return null;

  const perms = { canPayApi: panel.approver.canPayApi, canMarkPaid: panel.approver.canMarkPaid, canReject: panel.approver.canReject };
  const teto = panel.apiLimit;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f3f5f8', pb: selecionados.length ? 'calc(190px + env(safe-area-inset-bottom))' : 4 }}>
      {/* Cabeçalho */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#1f2a37', color: '#fff', boxShadow: 1 }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>Pagamentos a aprovar</Typography>
            <Typography variant="caption" sx={{ opacity: 0.75 }} noWrap component="div">{panel.approver.company} · {panel.approver.name}</Typography>
          </Box>
          <Tooltip title="Atualizar">
            <span><IconButton color="inherit" onClick={() => carregarPainel()} disabled={atualizando}><RefreshIcon /></IconButton></span>
          </Tooltip>
          <Tooltip title="Sair deste aparelho"><IconButton color="inherit" onClick={sair}><LogoutIcon /></IconButton></Tooltip>
        </Box>
        {atualizando && <LinearProgress sx={{ height: 2 }} />}
      </Box>

      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 1.5, sm: 2 }, pt: 2 }}>
        {erroGeral && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroGeral(null)}>{erroGeral}</Alert>}
        {avisoGeral && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAvisoGeral(null)}>{avisoGeral}</Alert>}

        {panel.efiTransfer?.enabled && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            <Button variant="outlined" size="small" onClick={() => setTransferindoEfi(true)}>
              Transferir para a Efí
            </Button>
          </Box>
        )}


        {/* Resumo */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 1.25, mb: 2 }}>
          <Kpi titulo="Saldo Sicoob" icon={<BankIcon sx={{ fontSize: 16 }} />}
            valor={saldoDisp != null ? brl(saldoDisp) : '—'}
            sub={panel.saldo.error ? panel.saldo.error : panel.saldo.atualizadoEm ? `às ${horaBR(panel.saldo.atualizadoEm)}${panel.saldo.stale ? ' (desatualizado)' : ''}` : undefined} />
          <Kpi titulo="Teto da API hoje" icon={<LimitIcon sx={{ fontSize: 16 }} />} valor={brl(teto.available)}
            sub={`usado ${brl(teto.used)} de ${brl(teto.limit)}`}>
            <LinearProgress variant="determinate" value={Math.min(100, (teto.used / teto.limit) * 100)}
              color={teto.available < 1000 ? 'error' : 'primary'} sx={{ mt: 0.75, height: 5, borderRadius: 3 }} />
          </Kpi>
          <Kpi titulo="Vencido" valor={brl(soma(vencidos))} cor={vencidos.length ? '#c62828' : undefined}
            sub={`${vencidos.length} título${vencidos.length === 1 ? '' : 's'}`} onClick={() => setFiltros({ ...filtros, periodo: 'vencidos' })} />
          <Kpi titulo="Vence hoje" valor={brl(soma(hoje))} cor={hoje.length ? '#b25c00' : undefined}
            sub={`${hoje.length} título${hoje.length === 1 ? '' : 's'}`} onClick={() => setFiltros({ ...filtros, periodo: 'hoje' })} />
          <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
            <Kpi titulo="Próximos 7 dias" valor={brl(soma(prox7))}
              sub={`${prox7.length} título${prox7.length === 1 ? '' : 's'} · até ${dataBR(in7)}`} onClick={() => setFiltros({ ...filtros, periodo: '7dias' })} />
          </Box>
        </Box>

        <Filtros value={filtros} onChange={setFiltros} favorecidos={favorecidos} contagem={contagem} />

        {/* Lista */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {filtrados.length} de {items.length} pendente{items.length === 1 ? '' : 's'} · {brl(soma(filtrados))}
          </Typography>
          {filtrados.length > 0 && (
            <Button size="small" onClick={() => setSel((s) => {
              const n = new Set(s);
              const alvo = filtrados.filter((i) => i.status !== 'processing');
              if (todosVisiveisSel) alvo.forEach((i) => n.delete(i.id)); else alvo.forEach((i) => n.add(i.id));
              return n;
            })}>
              {todosVisiveisSel ? 'Desmarcar visíveis' : 'Selecionar visíveis'}
            </Button>
          )}
        </Box>

        {filtrados.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <EmptyIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography color="text.secondary">{items.length ? 'Nenhum pedido com esses filtros.' : 'Nenhuma pendência. Tudo em dia!'}</Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25, alignItems: 'start' }}>
            {filtrados.map((i) => (
              <ItemCard key={i.id} item={i} selected={sel.has(i.id)} onToggle={() => toggle(i.id)}
                onOpen={() => setDetalhe(i)} />
            ))}
          </Box>
        )}

        {/* Pagos hoje */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <DoneIcon color="success" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>Pagos hoje</Typography>
            <Chip size="small" label={panel.paidToday.length} />
          </Box>
          {panel.paidToday.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Nenhum pagamento registrado hoje.</Typography>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
              {panel.paidToday.map((p, idx) => (
                <React.Fragment key={p.id}>
                  {idx > 0 && <Divider />}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{p.beneficiaryName || p.description || p.kindLabel}</Typography>
                      <Typography variant="caption" color="text.secondary" component="div">
                        {horaBR(p.paidAt)} · {p.channel === 'app' ? 'pago no app' : 'pela API'}{p.approvedByName ? ` · ${p.approvedByName}` : ''}
                        {p.bankDocument ? ` · doc ${p.bankDocument}` : ''}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{brl(p.amount)}</Typography>
                    <Tooltip title="Comprovante">
                      <IconButton size="small" href={p.pdfUrl} target="_blank" rel="noopener noreferrer"><PdfIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Box>
                </React.Fragment>
              ))}
            </Paper>
          )}
        </Box>
      </Box>

      {/* Barra da seleção, com as ações em lote */}
      {selecionados.length > 0 && (
        <Paper elevation={8} sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 20, borderRadius: 0, borderTop: '1px solid #dfe5ec' }}>
          <Box sx={{ maxWidth: 1100, mx: 'auto', px: 2, pt: 1.25, pb: 'calc(10px + env(safe-area-inset-bottom))' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">{selecionados.length} selecionado{selecionados.length === 1 ? '' : 's'}</Typography>
                <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{brl(totalSel)}</Typography>
              </Box>
              <Button size="small" onClick={() => setSel(new Set())}>Limpar</Button>
            </Stack>
            {saldoDisp != null && totalSel > saldoDisp && (
              <Alert severity="error" sx={{ mt: 1, py: 0 }}>Passa do saldo disponível ({brl(saldoDisp)}) em {brl(totalSel - saldoDisp)}.</Alert>
            )}
            {totalSelApi > teto.available && (
              <Alert severity="warning" sx={{ mt: 1, py: 0 }}>Os PIX pela API selecionados ({brl(totalSelApi)}) passam do que cabe no teto hoje ({brl(teto.available)}).</Alert>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {perms.canReject && (
                <Button variant="outlined" color="error" onClick={() => setRejeitar(selecionados.filter((i) => i.status === 'pending'))}>
                  Rejeitar
                </Button>
              )}
              {perms.canPayApi && (
                <Button variant="contained" color="success" sx={{ flex: 1 }} disabled={!selApi.length}
                  onClick={() => setLote(selApi)}>
                  {selApi.length ? `Aprovar ${selApi.length} PIX · ${brl(totalSelApi)}` : 'Nenhum PIX pela API'}
                </Button>
              )}
            </Stack>
            {selApp.length > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                {selApp.length} para pagar no app: dê baixa um a um (abra o item e toque em "Paguei no app").
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      <ItemDetail token={token} item={detalhe} perms={perms} onClose={() => setDetalhe(null)} onSessionLost={sessaoPerdida}
        onAprovar={(i, r) => { setConferirReview(r); setConferir(i); }} onPaguei={(i) => setPaguei(i)} onRejeitar={(i) => setRejeitar([i])} />
      <BulkApproveDialog token={token} items={lote}
        onClose={() => { setLote(null); setSel(new Set()); }} onDone={carregarPainel} onSessionLost={sessaoPerdida} />
      <ConferirDialog token={token} item={conferir} preReview={conferirReview} onClose={() => { setConferir(null); setConferirReview(null); }} onDone={carregarPainel} onSessionLost={sessaoPerdida} />
      <PagueiDialog token={token} item={paguei} onClose={() => setPaguei(null)} onDone={carregarPainel} onSessionLost={sessaoPerdida} />
      <RejeitarDialog token={token} items={rejeitar} onClose={() => { setRejeitar(null); setSel(new Set()); }} onDone={carregarPainel} onSessionLost={sessaoPerdida} />
      {panel.efiTransfer?.enabled && (
        <TransferirEfiDialog
          token={token}
          open={transferindoEfi}
          saldoSicoob={panel.saldo}
          saldoEfi={panel.efiTransfer.saldoEfi}
          onClose={() => setTransferindoEfi(false)}
          onDone={(mensagem) => { setTransferindoEfi(false); setAvisoGeral(mensagem); carregarPainel(); }}
          onSessionLost={() => { setTransferindoEfi(false); sessaoPerdida(); }}
        />
      )}
    </Box>
  );
};

export default AprovarPage;
